// These helpers are calling FTW's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import { types as sdkTypes, transit } from './sdkLoader';
import config from '../config';
import Decimal from 'decimal.js';

export const apiBaseUrl = () => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // In SSR context (no window), use canonical root URL
  if (typeof window === 'undefined') {
    return process.env.REACT_APP_CANONICAL_ROOT_URL || 'http://localhost:3000';
  }

  // Otherwise, use the same domain and port as the frontend
  return `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: config.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

// Use universal fetch (works in both browser and Node.js 18+)
const universalFetch = typeof window !== 'undefined' ? window.fetch : fetch;

const post = (path, body) => {
  const url = `${apiBaseUrl()}${path}`;
  const options = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/transit+json',
    },
    body: serialize(body),
  };
  return universalFetch(url, options)
    .then(res => {
      if (res.status >= 400) {
        const e = new Error('Local API request failed');
        e.apiResponse = res;
        throw e;
      }
      return res;
    })
    .then(res => {
      const contentTypeHeader = res.headers.get('Content-Type');
      const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;
      if (contentType === 'application/transit+json') {
        return res.text().then(deserialize);
      } else if (contentType === 'application/json') {
        return res.json();
      }
      return res.text();
    });
};

const get = path => {
  const url = `${apiBaseUrl()}${path}`;
  const options = {
    method: 'GET',
    credentials: 'include',
  };
  return universalFetch(url, options)
    .then(res => {
      if (res.status >= 400) {
        const e = new Error('Local API request failed');
        e.apiResponse = res;
        throw e;
      }
      return res;
    })
    .then(res => {
      const contentTypeHeader = res.headers.get('Content-Type');
      const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;
      if (contentType === 'application/transit+json') {
        return res.text().then(deserialize);
      } else if (contentType === 'application/json') {
        return res.json();
      }
      return res.text();
    });
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the booking data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

export const projectInitiatePrivileged = body => {
  return post('/api/project-initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

export const projectTransitionPrivileged = body => {
  return post('/api/project-transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Flex with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

export const markTxUnread = body => {
  return post(`/api/mark-tx-unread`, body);
};

export const markTxRead = (transactionId, recipientId) => {
  return get(`/api/mark-tx-read/${transactionId}/${recipientId}`);
};

export const fetchUnreadTxIds = recipientId => {
  return get(`/api/fetch-unread-tx-ids/${recipientId}`);
};

export const fetchOwnReview = transactionId => {
  return get(`/api/own-review/${transactionId}`);
};

export const markApplicationNew = body => {
  return post(`/api/mark-application-new`, body);
};

export const markApplicationOpened = (transactionId, recipientId) => {
  return get(`/api/mark-application-opened/${transactionId}/${recipientId}`);
};

export const fetchNewApplicationTxIds = recipientId => {
  return get(`/api/fetch-new-application-tx-ids/${recipientId}`);
};

export const fetchListingApplicationCount = listingId => {
  return get(`/api/listing-application-count/${listingId}`);
};

export const completePayoutTransaction = transactionId => {
  return get(`/api/complete-payout-transaction/${transactionId}`);
};

export const cancelPayoutTransaction = transactionId => {
  return get(`/api/cancel-payout-transaction/${transactionId}`);
};

export const getUsersForLandingPage = () => {
  return get(`/api/get-users-for-landing-page`);
};

export const getUsersForAiPage = () => {
  return get(`/api/get-users-for-ai-page`);
};

export const generateIntercomHmac = userId => {
  return get(`/api/generate-intercom-hmac/${userId}`);
};

// Track an Intercom event for the current user (resolved from the session
// cookie on the backend). See `server/api/intercom-track-event.js`.
export const trackIntercomEvent = body => {
  return post('/api/intercom-track-event', body);
};

// Update the current user's Intercom contact attributes (user resolved from
// the session cookie on the backend). See `server/api/intercom-update-user.js`.
export const updateIntercomUser = body => {
  return post('/api/intercom-update-user', body);
};

export const fetchProfileUserName = userId => {
  return get(`/api/fetch-profile-user-name/${userId}`);
};

export const requestOffPlatformTestimonial = body => {
  return post(`/api/request-off-platform-testimonial`, body);
};

export const fetchOffPlatformTestimonials = freelancerId => {
  return get(`/api/fetch-off-platform-testimonials/${freelancerId}`);
};

export const fetchcurrentOffPlatformTestimonial = (freelancerId, workExperienceId) => {
  return get(`/api/fetch-current-off-platform-testimonial/${freelancerId}/${workExperienceId}`);
};

export const leaveOffPlatformTestimonial = body => {
  return post(`/api/leave-off-platform-testimonial`, body);
};

export const fetchOffPlatformReviews = freelancerId => {
  return get(`/api/fetch-off-platform-reviews/${freelancerId}`);
};

export const leaveOffPlatformReview = body => {
  return post(`/api/leave-off-platform-review`, body);
};

export const fetchSkills = () => {
  return get(`/api/fetch-skills`);
};

export const fetchTools = () => {
  return get(`/api/fetch-tools`);
};

export const fetchSkillCategories = () => {
  return get(`/api/fetch-skill-categories`);
};

export const fetchSkillsAndToolsSuggestions = searchTerm => {
  return get(
    `/api/fetch-skills-and-tools-suggestions?searchTerm=${encodeURIComponent(searchTerm)}`
  );
};

export const calculateProfileTier = body => {
  return post(`/api/calculate-profile-tier`, body);
};

export const calculatePortfolioTier = body => {
  return post(`/api/calculate-portfolio-tier`, body);
};

export const deleteAccount = body => {
  return post('/api/delete-account', body);
};

export const updateProfileLive = body => {
  return post('/api/update-profile-live', body);
};

export const updateShowAtMarketPlace = body => {
  return post('/api/update-show-at-marketplace', body);
};

export const checkEmailSpam = body => {
  return post('/api/check-email-spam', body);
};

export const completeOnboarding = body => {
  return post('/api/complete-onboarding', body);
};

export const completeBasicInfo = body => {
  return post('/api/complete-basic-info', body);
};

export const prhLookup = businessId => {
  return post('/api/prh-lookup', { businessId });
};

export const saveTaxInformation = body => {
  return post('/api/save-tax-information', body);
};

export const lookupInvoicingCompany = body => {
  return post('/api/lookup-invoicing-company', body);
};

export const requestInvoicing = body => {
  return post('/api/request-invoicing', body);
};

export const acceptOfferInvoice = body => {
  return post('/api/accept-offer-invoice', body);
};

export const vatPreview = body => {
  return post('/api/vat-preview', body);
};

// A transaction's receipt or billing basis, generated on request (downloadable
// receipts spec §5.1). Resolves to the PDF as a Blob; rejects like get().
export const downloadTransactionReceipt = ({ transactionId, locale }) => {
  const url = `${apiBaseUrl()}/api/transactions/${encodeURIComponent(
    transactionId
  )}/receipt?locale=${encodeURIComponent(locale)}`;
  return universalFetch(url, { method: 'GET', credentials: 'include' }).then(res => {
    if (res.status >= 400) {
      const e = new Error('Local API request failed');
      e.apiResponse = res;
      throw e;
    }
    return res.blob();
  });
};

// Open an SSE stream against an endpoint that responds with `event:` / `data:` lines.
// `body` is sent as plain JSON. Returns an AbortController so callers can cancel.
//
// Callbacks: onEvent(eventName, data), onDone(), onError(err)
export const streamPost = (path, body, { onEvent, onDone, onError } = {}) => {
  const controller = new AbortController();
  const url = `${apiBaseUrl()}${path}`;

  (async () => {
    try {
      const res = await universalFetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (res.status >= 400) {
        const text = await res.text().catch(() => '');
        throw new Error(`Stream request failed (${res.status}): ${text}`);
      }
      if (!res.body || typeof res.body.getReader !== 'function') {
        throw new Error('Streaming not supported in this environment');
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let currentEvent = 'message';

      const dispatch = rawData => {
        if (!rawData) return;
        let parsed;
        try {
          parsed = JSON.parse(rawData);
        } catch (e) {
          parsed = rawData;
        }
        onEvent && onEvent(currentEvent, parsed);
        currentEvent = 'message';
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });

        let sepIdx;
        // SSE frames are separated by a blank line (\n\n)
        while ((sepIdx = buf.indexOf('\n\n')) !== -1) {
          const frame = buf.slice(0, sepIdx);
          buf = buf.slice(sepIdx + 2);

          const dataLines = [];
          frame.split('\n').forEach(line => {
            if (line.startsWith('event:')) {
              currentEvent = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              dataLines.push(line.slice(5).trim());
            }
          });
          dispatch(dataLines.join('\n'));
        }
      }
      onDone && onDone();
    } catch (e) {
      if (e.name === 'AbortError') return;
      onError && onError(e);
    }
  })();

  return controller;
};

export const jobPostingChat = (body, callbacks) =>
  streamPost('/api/job-posting-chat', body, callbacks);

export const jobPostingDraft = (body, callbacks) =>
  streamPost('/api/job-posting-draft', body, callbacks);
